import { extractQueryOptions } from './utils';

/**
 * Optimistic-update **runtime** helpers for the RTK Query codegen pipeline.
 *
 * ## Why this lives here (and not under `scripts/codegen-rtk/`)
 *
 * There are two layers, kept deliberately separate:
 *
 *   • The **generator** — `scripts/codegen-rtk/openapi-enhancements.ts` — runs
 *     at build time (bun/node). It reads each mutation's `x-rtk-cache` config
 *     and *emits source* that calls the factories below, e.g.
 *     `onQueryStarted: optimisticCreate(api, { type: Tags.Comment, ... })`.
 *     It never imports this file; it only writes a string reference to it.
 *
 *   • The **runtime** — this file — is the library that emitted code actually
 *     calls in the browser. The generated `src/api/<service>/enhancements.autogen.ts`
 *     imports it via `~/redux/api/optimistic-updates`, so it must live under
 *     `src/` (the `~` alias → `./src`) and ship in the app bundle. Putting it in
 *     `scripts/` would break the import — that dir isn't bundled. It sits next
 *     to its sibling `utils.ts` (shared RTK runtime helpers it depends on),
 *     whereas `src/api/<service>/` holds generated-only output.
 *
 * ## Contract
 *
 * The generator only knows the cache *tag* (`type`), the id source (`idPath` →
 * `idArg`, or a literal `id`), the response/list draft paths derived from the
 * matching GET `provides` config, and a `synth` recipe for building a mid-flight
 * placeholder from the request body — so all shape knowledge is passed in as
 * declarative config and resolved generically here at runtime.
 *
 * These helpers never throw: every cache mutation is guarded so a shape that
 * doesn't match the expected path is a no-op rather than a crash. The
 * schema-derived `invalidatesTags` stays in place as the reconciling fallback,
 * so the worst case for a mismatch is "no optimistic win, normal refetch".
 */

/**
 * Minimal structural view of the parts of an RTK Query api the optimistic
 * helpers touch. Typed loosely on purpose: the generated module passes the
 * service's *root* api, whose endpoint map is empty at the type level, so the
 * precise `updateQueryData` / `selectInvalidatedBy` overloads aren't usable.
 */
export interface OptimisticApi {
  util: {
    // Rest-param signatures keep the useful return types while staying
    // assignable from any `createApi` result. The service's *root* api is
    // typed against an empty endpoint map (endpoints are injected later), so
    // its `updateQueryData` `endpointName` param resolves to `never` — a precise
    // structural signature here would reject it.
    selectInvalidatedBy: (
      ...args: any[]
    ) => Array<{ endpointName: string; originalArgs: unknown }>;
    // Typed `any`: the root api's `updateQueryData` first param resolves to
    // `never`, which no concrete param signature is assignable from. Callers
    // dispatch the result and (for rollback) read `.undo()` via a cast.
    updateQueryData: any;
    // Dispatched on failure (unless suppressed) to refetch server truth.
    invalidateTags: (...args: any[]) => unknown;
  };
}

interface OnQueryStartedLifecycle {
  dispatch: (action: unknown) => unknown;
  getState: () => unknown;
  queryFulfilled: Promise<{ data: unknown }>;
}

/** Lifecycle handler shape compatible with RTK Query's `onQueryStarted`. */
export type OnQueryStarted = (
  arg: unknown,
  lifecycle: OnQueryStartedLifecycle,
) => Promise<void>;

const LIST_ID = 'LIST';

/**
 * Recipe for synthesising a cache entity from the mutation request body before
 * the server responds. For JSON:API the request body's resource object (`data`)
 * shares the cached entity's `{ type, attributes }` shape, so the placeholder is
 * a faithful stand-in until the real response lands. Derived at codegen time
 * from the request/response schemas.
 */
/** Maps a merged attribute key onto a different key already on the cached row. */
interface AttributeAlias {
  from: string;
  to: string;
}

interface SynthConfig {
  /** Mutation-arg field holding the request body. */
  bodyArg: string;
  /** Dot-path within the body to the resource object (`''` = body is the resource). */
  dataPath: string;
  /** Key holding attributes on the resource + cached entity (`''` = flat). */
  attributesKey: string;
  /** JSON:API `type` literal to stamp on the synthesised entity. */
  typeLiteral?: string;
  /**
   * When a list/detail cache uses different field names than the mutation
   * response (e.g. search projections), copy merged `from` onto `to` when the
   * target row already exposes `to`. Declared per service in `x-rtk-cache`.
   */
  attributeAliases?: ReadonlyArray<AttributeAlias>;
}

interface CreateConfig {
  /** RTK cache tag type the created entity belongs to. */
  type: string;
  /** Dot-path into the mutation response where the created entity lives. */
  responsePath: string;
  /** Attribute holding the entity id (used to de-dupe list inserts). */
  idField?: string;
  /** Dot-path to the array inside cached LIST queries for this tag. */
  listPath?: string;
  /** Recipe for the mid-flight placeholder; absent → reconcile post-flight only. */
  synth?: SynthConfig;
}

interface UpdateConfig {
  type: string;
  /** Mutation-arg field that resolves the entity id (from `idPath`). */
  idArg?: string;
  /** Literal entity id (from `id`), used for fixed symbolic keys. */
  idLiteral?: string;
  /** Dot-path into the mutation response where the full entity lives. */
  responsePath: string;
  /** Dot-path to the entity inside cached detail queries for this tag. */
  detailPath?: string;
  /** Dot-path to the array inside cached LIST queries for this tag. */
  listPath?: string;
  /** Attribute holding the entity id on detail/list entities. */
  idField?: string;
  /** Recipe for the mid-flight attributes merge; absent → reconcile post-flight only. */
  synth?: SynthConfig;
}

interface DeleteConfig {
  type: string;
  idArg?: string;
  idLiteral?: string;
  listPath?: string;
  idField?: string;
}

/**
 * Per-call customizations an engineer can pass via `rtkQueryOptions` to tune the
 * generated optimistic behaviour. Mirrors `OptimisticUpdateOptions` in `types.ts`.
 */
interface OptimisticOptions {
  bailOutOfOptimisticUpdates?: boolean;
  onOptimisticUpdateSuccess?: () => void;
  onOptimisticUpdateFailure?: () => void;
  preventCacheEvictionOnOptimisticFailures?: boolean;
}

interface OptimisticPlan {
  /** Pre-flight patches to roll back if the request fails. */
  patches: Array<{ undo: () => void }>;
  /** Applied on success to reconcile the cache with the server response. */
  reconcile?: (result: { data: unknown }) => void;
}

type EvictionTags = ReadonlyArray<{ type: string; id?: string | number }>;

/**
 * Shared lifecycle scaffold for all three optimistic kinds. Centralises the
 * caller customizations so create/update/delete only describe *what* to patch:
 *
 *   • `bailOutOfOptimisticUpdates` → skip all cache work; `invalidatesTags`
 *     (still on the endpoint) handles invalidation on success.
 *   • on success → apply the reconcile step, then `onOptimisticUpdateSuccess`.
 *   • on failure → roll back the pre-flight patches; unless
 *     `preventCacheEvictionOnOptimisticFailures`, invalidate the entity tags so
 *     the UI refetches server truth; then `onOptimisticUpdateFailure`.
 */
async function runOptimistic(
  api: OptimisticApi,
  arg: unknown,
  lifecycle: OnQueryStartedLifecycle,
  evictionTags: EvictionTags,
  buildPlan: () => OptimisticPlan,
): Promise<void> {
  const options = optimisticOptionsOf(arg);
  if (options.bailOutOfOptimisticUpdates) return;

  const plan = buildPlan();
  try {
    const result = await lifecycle.queryFulfilled;
    plan.reconcile?.(result);
    options.onOptimisticUpdateSuccess?.();
  } catch {
    plan.patches.forEach((patch) => patch.undo());
    if (
      !options.preventCacheEvictionOnOptimisticFailures &&
      evictionTags.length > 0
    ) {
      lifecycle.dispatch(api.util.invalidateTags(evictionTags));
    }
    options.onOptimisticUpdateFailure?.();
  }
}

/**
 * Optimistic create. When a `synth` recipe is available, a placeholder built
 * from the request body (with a temporary id) is inserted into every cached
 * LIST query before the round-trip — then swapped for the real server entity on
 * success, or rolled back on error. Without `synth`, the real entity is simply
 * inserted on success (no mid-flight placeholder).
 */
export function optimisticCreate(
  api: OptimisticApi,
  config: CreateConfig,
): OnQueryStarted {
  return (arg, lifecycle) =>
    runOptimistic(
      api,
      arg,
      lifecycle,
      [{ type: config.type, id: LIST_ID }],
      () => {
        const { dispatch, getState } = lifecycle;
        const patches: Array<{ undo: () => void }> = [];
        let tempId: string | undefined;

        const placeholder =
          config.synth && config.listPath != null && config.idField != null
            ? synthesiseEntity(arg, config.synth, config.idField)
            : undefined;

        if (placeholder) {
          tempId = placeholder[config.idField!] as string;
          for (const { endpointName, originalArgs } of selectByTag(
            api,
            getState,
            config.type,
            LIST_ID,
          )) {
            patches.push(
              dispatch(
                api.util.updateQueryData(
                  endpointName,
                  originalArgs,
                  (draft: any) => {
                    arrayAt(draft, config.listPath!)?.push(clone(placeholder));
                  },
                ),
              ) as unknown as { undo: () => void },
            );
          }
        }

        return {
          patches,
          reconcile: (result) => {
            const entity = resolvePath(result.data, config.responsePath);
            if (entity == null || config.listPath == null) return;
            for (const { endpointName, originalArgs } of selectByTag(
              api,
              getState,
              config.type,
              LIST_ID,
            )) {
              dispatch(
                api.util.updateQueryData(
                  endpointName,
                  originalArgs,
                  (draft: any) => {
                    const list = arrayAt(draft, config.listPath!);
                    if (!list) return;
                    // Swap the placeholder for the real entity if we inserted one.
                    if (tempId != null && config.idField != null) {
                      const index = list.findIndex(
                        (item) => item?.[config.idField!] === tempId,
                      );
                      if (index >= 0) {
                        list[index] = clone(entity);
                        return;
                      }
                    }
                    if (!alreadyPresent(list, config.idField, entity)) {
                      list.push(clone(entity));
                    }
                  },
                ),
              );
            }
          },
        };
      },
    );
}

/**
 * Optimistic update. When a `synth` recipe is available, the request body's
 * attributes are merged into the cached detail entry and list item before the
 * round-trip (rolled back on error). On success the detail cache is replaced
 * with the full server entity, while LIST items merge the response attributes
 * into the existing row so projection-shaped caches (e.g. ticket search) keep
 * fields the detail response omits (EKP-56387).
 */
export function optimisticUpdate(
  api: OptimisticApi,
  config: UpdateConfig,
): OnQueryStarted {
  return (arg, lifecycle) => {
    const id = resolveId(arg, config.idArg, config.idLiteral);
    const evictionTags: EvictionTags =
      id != null
        ? [
            { type: config.type, id },
            { type: config.type, id: LIST_ID },
          ]
        : [{ type: config.type, id: LIST_ID }];

    return runOptimistic(api, arg, lifecycle, evictionTags, () => {
      const { dispatch, getState } = lifecycle;
      const patches: Array<{ undo: () => void }> = [];

      const attrs =
        config.synth && id != null
          ? synthesiseAttributes(arg, config.synth)
          : undefined;

      if (attrs && id != null) {
        const key = config.synth!.attributesKey;

        if (config.detailPath != null) {
          for (const { endpointName, originalArgs } of selectByTag(
            api,
            getState,
            config.type,
            id,
          )) {
            patches.push(
              dispatch(
                api.util.updateQueryData(
                  endpointName,
                  originalArgs,
                  (draft: any) => {
                    applyAttributes(
                      resolvePath(draft, config.detailPath!),
                      key,
                      attrs,
                      config.synth?.attributeAliases,
                    );
                  },
                ),
              ) as unknown as { undo: () => void },
            );
          }
        }

        if (config.listPath != null && config.idField != null) {
          for (const { endpointName, originalArgs } of selectByTag(
            api,
            getState,
            config.type,
            LIST_ID,
          )) {
            patches.push(
              dispatch(
                api.util.updateQueryData(
                  endpointName,
                  originalArgs,
                  (draft: any) => {
                    const item = arrayAt(draft, config.listPath!)?.find(
                      (entry) => entry?.[config.idField!] === id,
                    );
                    if (item && hasAttributeEnvelope(item, key)) {
                      applyAttributes(
                        item,
                        key,
                        attrs,
                        config.synth?.attributeAliases,
                      );
                    }
                  },
                ),
              ) as unknown as { undo: () => void },
            );
          }
        }
      }

      return {
        patches,
        reconcile: (result) => {
          const entity = resolvePath(result.data, config.responsePath);
          if (entity == null || id == null) return;

          if (config.detailPath != null) {
            for (const { endpointName, originalArgs } of selectByTag(
              api,
              getState,
              config.type,
              id,
            )) {
              dispatch(
                api.util.updateQueryData(
                  endpointName,
                  originalArgs,
                  (draft: any) => {
                    setPath(draft, config.detailPath!, clone(entity));
                  },
                ),
              );
            }
          }

          if (config.listPath != null && config.idField != null) {
            const attributesKey = config.synth?.attributesKey;
            for (const { endpointName, originalArgs } of selectByTag(
              api,
              getState,
              config.type,
              LIST_ID,
            )) {
              dispatch(
                api.util.updateQueryData(
                  endpointName,
                  originalArgs,
                  (draft: any) => {
                    const list = arrayAt(draft, config.listPath!);
                    if (!list) return;
                    const index = list.findIndex(
                      (item) => item?.[config.idField!] === id,
                    );
                    if (index < 0) return;
                    // Merge into the existing row when both sides are
                    // attribute envelopes — never replace a search projection
                    // with a detail-shaped entity (EKP-56387).
                    if (
                      attributesKey &&
                      mergeListItemAttributes(
                        list[index],
                        entity,
                        attributesKey,
                        config.synth?.attributeAliases,
                      )
                    ) {
                      return;
                    }
                    // Nested projections (e.g. workflow documents keyed by
                    // `document_id`) are flat rows. A JSON:API PUT that only
                    // returns `id` / `type` / `attributes` must merge into
                    // that row — replacing it drops `name` and crashes the
                    // documents panel (Sentry UI-806).
                    if (
                      mergeJsonApiAttributesIntoFlatRow(
                        list[index],
                        entity,
                        config.idField,
                      )
                    ) {
                      return;
                    }
                    list[index] = clone(entity);
                  },
                ),
              );
            }
          }
        },
      };
    });
  };
}

/**
 * Optimistic delete. Removes the entity from every cached LIST query before the
 * round-trip completes, then rolls every patch back if the request fails.
 */
export function optimisticDelete(
  api: OptimisticApi,
  config: DeleteConfig,
): OnQueryStarted {
  return (arg, lifecycle) => {
    const id = resolveId(arg, config.idArg, config.idLiteral);
    const evictionTags: EvictionTags =
      id != null
        ? [
            { type: config.type, id },
            { type: config.type, id: LIST_ID },
          ]
        : [{ type: config.type, id: LIST_ID }];

    return runOptimistic(api, arg, lifecycle, evictionTags, () => {
      const { dispatch, getState } = lifecycle;
      const patches: Array<{ undo: () => void }> = [];

      if (id != null && config.listPath != null && config.idField != null) {
        for (const { endpointName, originalArgs } of selectByTag(
          api,
          getState,
          config.type,
          LIST_ID,
        )) {
          patches.push(
            dispatch(
              api.util.updateQueryData(
                endpointName,
                originalArgs,
                (draft: any) => {
                  const list = arrayAt(draft, config.listPath!);
                  if (!list) return;
                  const index = list.findIndex(
                    (item) => item?.[config.idField!] === id,
                  );
                  if (index >= 0) list.splice(index, 1);
                },
              ),
            ) as unknown as { undo: () => void },
          );
        }
      }

      return { patches };
    });
  };
}

/**
 * Runs several optimistic handlers for a single mutation (e.g. an endpoint that
 * both deletes one entity and creates another). Each handler awaits the shared
 * `queryFulfilled` independently, so success/rollback stay correctly scoped.
 */
export function combineOptimisticHandlers(
  ...handlers: OnQueryStarted[]
): OnQueryStarted {
  return async (arg, lifecycle) => {
    await Promise.all(handlers.map((handler) => handler(arg, lifecycle)));
  };
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function selectByTag(
  api: OptimisticApi,
  getState: () => unknown,
  type: string,
  id: string | number,
) {
  return api.util.selectInvalidatedBy(getState(), [{ type, id }]);
}

/** Resolves a dot-path (any trailing `[]` ignored), e.g. `"data.items"`. */
function resolvePath(root: unknown, path: string | undefined): unknown {
  if (!path) return root;
  let current: any = root;
  for (const segment of path.split('.')) {
    if (current == null) return undefined;
    current = current[stripArraySuffix(segment)];
  }
  return current;
}

/** Sets the value at a dot-path, no-op if any intermediate segment is missing. */
function setPath(root: unknown, path: string, value: unknown): void {
  const segments = path.split('.').map(stripArraySuffix);
  let current: any = root;
  for (let i = 0; i < segments.length - 1; i += 1) {
    if (current == null) return;
    current = current[segments[i]];
  }
  if (current != null) current[segments[segments.length - 1]] = value;
}

function arrayAt(draft: unknown, listPath: string): any[] | undefined {
  const value = resolvePath(draft, listPath);
  return Array.isArray(value) ? value : undefined;
}

function alreadyPresent(
  list: any[],
  idField: string | undefined,
  entity: unknown,
): boolean {
  if (!idField) return false;
  const id = (entity as any)?.[idField];
  if (id == null) return false;
  return list.some((item) => item?.[idField] === id);
}

function resolveId(
  arg: unknown,
  idArg: string | undefined,
  idLiteral: string | undefined,
): string | number | undefined {
  if (idLiteral !== undefined) return idLiteral;
  if (!idArg) return undefined;
  const { payload } = extractQueryOptions(arg as any);
  const value = (payload as any)?.[idArg];
  return typeof value === 'string' || typeof value === 'number'
    ? value
    : undefined;
}

/** Per-call optimistic customizations from `rtkQueryOptions`. */
function optimisticOptionsOf(arg: unknown): OptimisticOptions {
  const { options } = extractQueryOptions(arg as any);
  return (options ?? {}) as OptimisticOptions;
}

function stripArraySuffix(segment: string): string {
  return segment.replace(/\[\]$/, '');
}

// --- Mid-flight synthesis ---

/** The resource object inside the request body (`{ type?, attributes }`). */
function requestResource(arg: unknown, synth: SynthConfig): any {
  const { payload } = extractQueryOptions(arg as any);
  const body = (payload as any)?.[synth.bodyArg];
  const resource = synth.dataPath ? resolvePath(body, synth.dataPath) : body;
  return resource != null && typeof resource === 'object'
    ? resource
    : undefined;
}

/**
 * Builds a placeholder cache entity from the request body: the resource object
 * (already `{ type, attributes }` for JSON:API) plus a temporary id and, if the
 * schema pins one and the body omitted it, the JSON:API `type`. Caller-supplied
 * `rtkQueryOptions.optimisticFields` are stamped on top (e.g. `author_id`).
 *
 * Cloned from the request body so the merge never mutates the in-flight arg.
 */
function synthesiseEntity(
  arg: unknown,
  synth: SynthConfig,
  idField: string,
): Record<string, unknown> | undefined {
  const resource = requestResource(arg, synth);
  if (!resource) return undefined;
  const entity = clone(resource) as Record<string, unknown>;
  entity[idField] = makeTempId();
  if (synth.typeLiteral != null && entity.type == null) {
    entity.type = synth.typeLiteral;
  }
  const extra = optimisticFieldsOf(arg);
  if (extra) applyAttributes(entity, synth.attributesKey, extra);
  return entity;
}

/**
 * The attributes to merge for an optimistic update: the request body's
 * attributes with any caller-supplied `optimisticFields` layered on top.
 * Cloned so the cached entry never aliases the in-flight request body.
 */
function synthesiseAttributes(arg: unknown, synth: SynthConfig): any {
  const resource = requestResource(arg, synth);
  let base: unknown;
  if (resource) {
    base = synth.attributesKey ? resource[synth.attributesKey] : resource;
  }
  const baseObj = base != null && typeof base === 'object' ? base : undefined;
  const extra = optimisticFieldsOf(arg);
  if (!baseObj && !extra) return undefined;
  return clone({ ...(baseObj ?? {}), ...(extra ?? {}) });
}

/** Caller-supplied placeholder fields from `rtkQueryOptions.optimisticFields`. */
function optimisticFieldsOf(arg: unknown): Record<string, unknown> | undefined {
  const { options } = extractQueryOptions(arg as any);
  const fields = (options as { optimisticFields?: unknown }).optimisticFields;
  return fields != null && typeof fields === 'object'
    ? (fields as Record<string, unknown>)
    : undefined;
}

/** Shallow-merges `attrs` into the cached entity's attributes (or its root). */
function applyAttributes(
  entity: unknown,
  attributesKey: string,
  attrs: Record<string, unknown>,
  attributeAliases?: ReadonlyArray<AttributeAlias>,
): void {
  if (entity == null || typeof entity !== 'object') return;
  const target = attributesKey
    ? ((entity as any)[attributesKey] ??= {})
    : (entity as any);
  Object.assign(target, attrs);
  applyAttributeAliases(target, attrs, attributeAliases);
}

function applyAttributeAliases(
  target: Record<string, unknown>,
  attrs: Record<string, unknown>,
  aliases: ReadonlyArray<AttributeAlias> | undefined,
): void {
  if (!aliases?.length) return;
  for (const { from, to } of aliases) {
    if (
      Object.prototype.hasOwnProperty.call(attrs, from) &&
      Object.prototype.hasOwnProperty.call(target, to) &&
      !Object.prototype.hasOwnProperty.call(attrs, to)
    ) {
      target[to] = attrs[from];
    }
  }
}

function hasAttributeEnvelope(entity: unknown, attributesKey: string): boolean {
  if (entity == null || typeof entity !== 'object') {
    return false;
  }
  if (!attributesKey) {
    return true;
  }
  const attrs = (entity as Record<string, unknown>)[attributesKey];
  return attrs != null && typeof attrs === 'object';
}

/**
 * Merges a JSON:API resource's `attributes` onto a flat cached list row when
 * the row identifies itself with `idField` (e.g. `document_id`) that the
 * envelope does not carry at the top level. Returns false when the shapes
 * already match so the caller can fall back to replace.
 */
function mergeJsonApiAttributesIntoFlatRow(
  existing: unknown,
  entity: unknown,
  idField: string | undefined,
): boolean {
  if (
    !idField ||
    existing == null ||
    typeof existing !== 'object' ||
    entity == null ||
    typeof entity !== 'object'
  ) {
    return false;
  }
  if (hasAttributeEnvelope(existing, 'attributes')) {
    return false;
  }
  const existingRecord = existing as Record<string, unknown>;
  const entityRecord = entity as Record<string, unknown>;
  if (existingRecord[idField] == null || entityRecord[idField] != null) {
    return false;
  }
  const attrs = entityRecord.attributes;
  if (attrs == null || typeof attrs !== 'object') {
    return false;
  }
  Object.assign(existingRecord, attrs);
  return true;
}

/**
 * Merges response attributes into an existing LIST row. Returns false when the
 * shapes aren't attribute envelopes so the caller can fall back to replace.
 */
function mergeListItemAttributes(
  existing: unknown,
  entity: unknown,
  attributesKey: string,
  attributeAliases?: ReadonlyArray<AttributeAlias>,
): boolean {
  if (
    existing == null ||
    typeof existing !== 'object' ||
    entity == null ||
    typeof entity !== 'object' ||
    !hasAttributeEnvelope(existing, attributesKey)
  ) {
    return false;
  }
  const responseAttrs = (entity as Record<string, unknown>)[attributesKey];
  if (responseAttrs == null || typeof responseAttrs !== 'object') {
    return false;
  }
  applyAttributes(
    existing,
    attributesKey,
    responseAttrs as Record<string, unknown>,
    attributeAliases,
  );
  return true;
}

let tempIdCounter = 0;

/** Collision-resistant placeholder id, replaced by the server id on success. */
function makeTempId(): string {
  tempIdCounter += 1;
  return `optimistic-${Date.now()}-${tempIdCounter}`;
}

/** Deep-clones a plain entity so the same object isn't shared across caches. */
function clone<T>(value: T): T {
  return structuredClone(value);
}
